import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { IEvent } from '@app/api/interfaces/event.interface';
import { LdTabs } from '@app/main/leak-detection/enums/ld-tabs';
import { IMapBounds } from '@app/main/main-content/map/interfaces/map-bounds.interface';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { IPosition } from '@app/core/interfaces/position.interface';
import { IMainTableCellContext } from '@app/main/main-content/main-table/interfaces/main-table-cell-context.interface';

export enum LdActionType {
  ResetSelection = 'ResetSelection',
  TabChange = 'TabChange',
  CorrelationClick = 'CorrelationClick',
  CorrelationCtrlClick = 'CorrelationCtrlClick',
  CorrelationDbClick = 'CorrelationDbClick',
  LoggerClick = 'LoggerClick',
  LoggerCtrlClick = 'LoggerCtrlClick',
  LoggerDbClick = 'LoggerDbClick',
  EventClick = 'EventClick',
  EventDbClick = 'EventDbClick',
  CellIdClick = 'CellIdClick',
  CellIdLoggerClick = 'CellIdLoggerClick',
  CellIdVisibilityClick = 'CellIdVisibilityClick',
  ZoomToFitItem = 'ZoomToFitItem',
  ShowInStreetView = 'ShowInStreetView',
  ShowPhotos = 'ShowPhotos',
  ChangedMapBounds = 'ChangedMapBounds',
  LoggerContextMenu = 'LoggerContextMenu',
  CorrelationContextMenu = 'CorrelationContextMenu',
  EventContextMenu = 'EventContextMenu',
  MapAreaContextMenu = 'MapAreaContextMenu',
}

export enum LdActionPlace {
  TabsPanel = 'TabsPanel',
  Map = 'Map',
  FloatingPopup = 'FloatingPopup',
  OptionsPopover = 'OptionsPopover',
}

export interface LdCoordinatorActionCellIdClick {
  type: LdActionType.CellIdClick;
  place: LdActionPlace;
  item: string;
}

export interface LdCoordinatorActionCellIdLoggerClick {
  type: LdActionType.CellIdLoggerClick;
  place: LdActionPlace;
  item: ILdLogger;
}

export interface LdCoordinatorActionCellIdVisibilityClick {
  type: LdActionType.CellIdVisibilityClick;
  place: LdActionPlace;
  items: string[] | null;
}

export interface LdCoordinatorActionResetSelection {
  type: LdActionType.ResetSelection;
}
export interface LdCoordinatorActionTabChange {
  type: LdActionType.TabChange;
  place: LdActionPlace;
  tab: LdTabs;
}
export interface LdCoordinatorActionCorrelationClick {
  type: LdActionType.CorrelationClick;
  place: LdActionPlace;
  item: ICorrelation;
}
export interface LdCoordinatorActionCorrelationCtrlClick {
  type: LdActionType.CorrelationCtrlClick;
  place: LdActionPlace;
  item: ICorrelation;
}
export interface LdCoordinatorActionCorrelationDbClick {
  type: LdActionType.CorrelationDbClick;
  place: LdActionPlace;
  item: ICorrelation;
}
export interface LdCoordinatorActionLoggerClick {
  type: LdActionType.LoggerClick;
  place: LdActionPlace;
  item: ILdLogger;
}
export interface LdCoordinatorActionLoggerCtrlClick {
  type: LdActionType.LoggerCtrlClick;
  place: LdActionPlace;
  item: ILdLogger;
}
export interface LdCoordinatorActionLoggerDbClick {
  type: LdActionType.LoggerDbClick;
  place: LdActionPlace;
  item: ILdLogger;
}
export interface LdCoordinatorActionEventClick {
  type: LdActionType.EventClick;
  place: LdActionPlace;
  item: IEvent;
}
export interface LdCoordinatorActionEventDbClick {
  type: LdActionType.EventDbClick;
  place: LdActionPlace;
  item: IEvent;
}
export interface LdCoordinatorActionZoomToFitItem {
  type: LdActionType.ZoomToFitItem;
  tab: LdTabs;
  item: ICorrelation | ILdLogger | IEvent;
}
/** the map opens its street view at `position` — which entity it came from does not matter downstream */
export interface LdCoordinatorActionShowInStreetView {
  type: LdActionType.ShowInStreetView;
  place: LdActionPlace;
  position: IPosition;
}
export interface LdCoordinatorActionShowPhotos {
  type: LdActionType.ShowPhotos;
  place: LdActionPlace;
  item: ILdLogger;
}
export interface LdCoordinatorActionChangedMapBounds {
  type: LdActionType.ChangedMapBounds;
  bounds: IMapBounds;
}

/** `event` is the original `contextmenu` MouseEvent — the menu overlay is positioned by it */
interface LdCoordinatorActionContextMenuBase {
  place: LdActionPlace;
  event: MouseEvent;
  /** set when the right click happened inside a table data cell */
  cell?: IMainTableCellContext;
}

export interface LdCoordinatorActionLoggerContextMenu extends LdCoordinatorActionContextMenuBase {
  type: LdActionType.LoggerContextMenu;
  item: ILdLogger;
}
export interface LdCoordinatorActionCorrelationContextMenu extends LdCoordinatorActionContextMenuBase {
  type: LdActionType.CorrelationContextMenu;
  item: ICorrelation;
}
export interface LdCoordinatorActionEventContextMenu extends LdCoordinatorActionContextMenuBase {
  type: LdActionType.EventContextMenu;
  item: IEvent;
}
export interface LdCoordinatorActionMapAreaContextMenu extends LdCoordinatorActionContextMenuBase {
  type: LdActionType.MapAreaContextMenu;
  position: IPosition;
}

export type LdCoordinatorActions =
  | LdCoordinatorActionResetSelection
  | LdCoordinatorActionTabChange
  | LdCoordinatorActionLoggerClick
  | LdCoordinatorActionLoggerCtrlClick
  | LdCoordinatorActionLoggerDbClick
  | LdCoordinatorActionCorrelationClick
  | LdCoordinatorActionCorrelationCtrlClick
  | LdCoordinatorActionCorrelationDbClick
  | LdCoordinatorActionEventClick
  | LdCoordinatorActionEventDbClick
  | LdCoordinatorActionCellIdClick
  | LdCoordinatorActionCellIdLoggerClick
  | LdCoordinatorActionCellIdVisibilityClick
  | LdCoordinatorActionZoomToFitItem
  | LdCoordinatorActionShowInStreetView
  | LdCoordinatorActionShowPhotos
  | LdCoordinatorActionChangedMapBounds
  | LdCoordinatorActionLoggerContextMenu
  | LdCoordinatorActionCorrelationContextMenu
  | LdCoordinatorActionEventContextMenu
  | LdCoordinatorActionMapAreaContextMenu;

/**
 * `LdCoordinatorStore.dispatch` as seen by config providers (context menus, …) — they get the real
 * dispatcher instead of an encapsulated command callback, so a menu entry dispatches an existing
 * coordinator action when it needs one, and does its own work otherwise.
 */
export type LdCoordinatorDispatch = (action: LdCoordinatorActions) => void;
